/**
 * Browser Demo account: sign-in, arrival flag, and MSA test-data toggles.
 * Private coverage examples are separate from public Demo sign-in and live records.
 */
import {
  BROWSER_DEMO_USER_ID,
  DEMO_ARRIVAL_STORAGE_KEY,
  MSA_TEST_DATA_FIXTURE_KEY,
  MSA_TEST_DATA_FIXTURES,
  MSA_TEST_DATA_MODE_KEY,
  MSA_TEST_DATA_EMAILS,
  isBrowserDemoUserId,
  type MsaTestDataFixture,
} from '../../shared/demoAccount';
import { activeSavedAccount, completeAccountSignIn, isAccountExpired, readAccountUser, signOutAccount } from './accountSessions';

export {
  BROWSER_DEMO_USER_ID,
  DEMO_ARRIVAL_STORAGE_KEY,
  MSA_TEST_DATA_FIXTURES,
  isBrowserDemoUserId,
};
export type { MsaTestDataFixture };

function storageGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

/** Mark that this browser arrived via the landing/app Demo control. */
export function markDemoArrival(): void {
  storageSet(DEMO_ARRIVAL_STORAGE_KEY, String(Date.now()));
}

export function clearDemoArrival(): void {
  storageSet(DEMO_ARRIVAL_STORAGE_KEY, null);
}

export function arrivedViaDemoClick(): boolean {
  const raw = storageGet(DEMO_ARRIVAL_STORAGE_KEY);
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && at > 0;
}

export function isDemoUserSignedIn(): boolean {
  return isBrowserDemoUserId(readAccountUser()?.id);
}

/**
 * Private testing chrome only. This does not grant any server permissions.
 * Read the active session's email, never a company domain or profile-card email.
 */
export function shouldAllowDemoFixtures(): boolean {
  const account = activeSavedAccount();
  return Boolean(account && !isAccountExpired(account)
    && MSA_TEST_DATA_EMAILS.includes(String(account.user.email || '').trim().toLowerCase()));
}

function testDataKey(key: string): string | null {
  if (!shouldAllowDemoFixtures()) return null;
  return `${key}:${encodeURIComponent(activeSavedAccount()!.user.id)}`;
}

export const MSA_TEST_DATA_CHANGED_EVENT = 'ziawellnav-msa-test-data-changed';
function testDataChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(MSA_TEST_DATA_CHANGED_EVENT));
}

/** Same localStorage key App.tsx / activeUsersService use for the 30-minute free demo. */
const FREE_MODE_STORAGE_KEY = 'zia_free_mode_expires_at';

function isFreeModeTimerActive(): boolean {
  const raw = storageGet(FREE_MODE_STORAGE_KEY);
  if (!raw) return false;
  const expiresAt = Number(raw);
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
}

/**
 * Demo chrome for product testing: browser-demo session, Demo-click arrival,
 * or an active free-mode timer. Elevates Operators + Background pills / Desk
 * entry to match a real operator without granting server AuthZ or fixtures.
 */
export function isBrowserDemoExperience(): boolean {
  return isDemoUserSignedIn() || arrivedViaDemoClick() || isFreeModeTimerActive();
}

export function isMsaTestDataModeEnabled(): boolean {
  const key = testDataKey(MSA_TEST_DATA_MODE_KEY);
  return Boolean(key && storageGet(key) === '1');
}

export function setMsaTestDataModeEnabled(on: boolean): void {
  const key = testDataKey(MSA_TEST_DATA_MODE_KEY);
  if (!key) return;
  storageSet(key, on ? '1' : null);
  if (!on) storageSet(testDataKey(MSA_TEST_DATA_FIXTURE_KEY)!, null);
  testDataChanged();
}

export function getSelectedMsaTestDataFixtureId(): string | null {
  const key = testDataKey(MSA_TEST_DATA_FIXTURE_KEY);
  const id = key ? storageGet(key) : null;
  if (!id) return null;
  return MSA_TEST_DATA_FIXTURES.some((row) => row.id === id) ? id : null;
}

export function setSelectedMsaTestDataFixtureId(id: string | null): void {
  const key = testDataKey(MSA_TEST_DATA_FIXTURE_KEY);
  if (!key) return;
  if (!id || !MSA_TEST_DATA_FIXTURES.some((row) => row.id === id)) {
    storageSet(key, null);
    testDataChanged();
    return;
  }
  storageSet(key, id);
  testDataChanged();
}

export function listMsaTestDataFixtures(): readonly MsaTestDataFixture[] {
  return MSA_TEST_DATA_FIXTURES;
}

/**
 * Mint / refresh the stable demo session and complete client sign-in.
 * Free-mode timer remains the access gate; this only establishes identity.
 */
export async function signInAsDemoUser(): Promise<{ userId: string }> {
  const response = await fetch('/api/auth/demo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'start' }),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.verified || !result?.sessionToken || !result?.user?.id) {
    throw new Error(result?.error || 'Demo sign-in is temporarily unavailable.');
  }
  if (!isBrowserDemoUserId(result.user.id)) {
    throw new Error('Demo sign-in returned an unexpected account.');
  }
  completeAccountSignIn({
    user: {
      id: String(result.user.id),
      username: String(result.user.username || 'demo'),
      first_name: String(result.user.firstName || result.user.first_name || 'Demo'),
      email: result.user.email ? String(result.user.email) : undefined,
    },
    token: String(result.sessionToken),
    expiresAt: String(result.expiresAt || ''),
    role: 'customer',
  });
  markDemoArrival();
  return { userId: BROWSER_DEMO_USER_ID };
}

/** Drop the demo session when free mode ends so paid gates stay honest. */
export function signOutDemoUserIfActive(): void {
  if (!isDemoUserSignedIn()) return;
  signOutAccount();
  clearDemoArrival();
  setMsaTestDataModeEnabled(false);
}
